// ** React Imports
import * as React from 'react'
import { InputAdornment, styled, TextField, Typography } from '@mui/material'
import TextFieldError from './TextFieldError'
import TextInputLimitCounter from './TextInputLimitCounter'

interface InputTextFieldProps {
  textFieldId: string
  inputValue?: any
  handleChange?: (value: any) => void
  inputType?: string
  isRequired?: boolean
  errorStatus?: boolean
  errorMessage?: string
  label?: React.ReactNode
  startIcon?: React.ReactNode
  endIcon?: JSX.Element
  inputProps?: any
  inputSize?: 'small' | 'medium'
  isFullWidth?: boolean
  isDisabled?: boolean
  inputModeType?: 'text' | 'search' | 'none' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal'
  inputPlaceholder?: string
  inputVairant?: 'outlined' | 'filled' | 'standard'
  handleFocus?: (e: any) => void
  handleBlur?: (e: any) => void
  maxCharacter?: number
  characterCounter?: boolean
  handleOnClick?: (e: any) => void
  handleKeyDown?: (e: any) => void
  autoFocus?: boolean
  inputMargin?: 'none' | 'normal' | 'dense'
  customClass?: string
  inputClass?: string
  autoComplete?: string
  isMultiline?: boolean
  delimiters?: Array<[string, string]>
  showCharacterCountConfig?: {
    maxLength: number
    showOnlyOnFocus: boolean
    customClass?: string
    showCharacterCountOnLabel?: boolean
  }
  minWordLimit?: number
  maxWordLimit?: number
  minCharacterLimit?: number
  maxCharacterLimit?: number
  fixedCharacterLimit?: number
  fixedNumberLimit?: number
  minCharacterPerWord?: number
  maxCharacterPerWord?: number
  customErrorMessage?: string
  toolTipRequired?: boolean
  labelCustomClass?: string
}

export const Input = styled(TextField)(({ theme, ...props }) => ({
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    display: 'none'
  },
  '& input[type=number]': {
    MozAppearance: 'textfield'
  },
  '.MuiOutlinedInput-notchedOutline': {
    border: 'solid',
    boxShadow: 'none',
    borderRadius: 'var(--flo-border-radius-lg)',
    borderWidth: 'var(--flo-border-width-2)',
    borderColor: 'var(--flo-gray-light-color)'
  },
  '.MuiInputBase-root': {
    fontSize: 'unset'
  },
  '& input': {
    borderRadius: '0px'
  }
}))

const InputTextField: React.FC<InputTextFieldProps> = ({
  textFieldId,
  isRequired = false,
  errorStatus = false,
  errorMessage = '',
  label = '',
  startIcon = <></>,
  endIcon = <></>,
  inputProps = {},
  inputSize = 'small',
  isFullWidth = true,
  inputValue,
  handleChange,
  isDisabled = false,
  inputType = 'text',
  inputModeType = 'text',
  inputPlaceholder = '',
  inputVairant = 'outlined',
  handleFocus,
  handleBlur,
  maxCharacter,
  characterCounter = false,
  handleOnClick,
  handleKeyDown,
  autoFocus = false,
  inputMargin = 'none',
  customClass = '',
  inputClass = '',
  autoComplete = 'off',
  isMultiline = false,
  showCharacterCountConfig,
  delimiters,
  minWordLimit,
  maxWordLimit,
  minCharacterLimit,
  maxCharacterLimit,
  fixedCharacterLimit,
  fixedNumberLimit,
  minCharacterPerWord,
  maxCharacterPerWord,
  customErrorMessage,
  toolTipRequired = true,
  labelCustomClass = ''
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const [localErrorMessage, setLocalErrorMessage] = React.useState('')

  const isDelimitedPart = React.useCallback(
    (part: string) => {
      if (part?.length < 2) return false
      const firstChar = part?.[0]
      const lastChar = part?.[part?.length - 1]
      return Boolean(delimiters?.some(([first, last]) => first === firstChar && last === lastChar))
    },
    [delimiters]
  )

  const getHighlightedText = () => {
    const parts = inputValue?.split(' ')
    return parts?.map((part: string, i: number) =>
      isDelimitedPart(part) ? (
        <React.Fragment key={i}>
          <span key={i} className='p-0 bg-gray-100 rounded-lg text-gray-dark outline-coal-lighter outline'>
            {part}
          </span>{' '}
        </React.Fragment>
      ) : (
        <React.Fragment key={i}>
          <span className='text-transparent'>{part}</span>{' '}
        </React.Fragment>
      )
    )
  }
  const showCount = () => {
    return (
      <span className={showCharacterCountConfig?.customClass}>
        {inputValue?.length}/{showCharacterCountConfig?.maxLength}
      </span>
    )
  }
  const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true)
    if (typeof handleFocus === 'function') {
      handleFocus(e)
    }
  }
  const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false)
    if (typeof handleBlur === 'function') {
      handleBlur(e)
    }
  }

  const inputRef = React.useRef<HTMLInputElement>(null)
  const prevCaretPosition = React.useRef(0)

  const handleCaretPosition = (caretPosition: number) => {
    if (!inputValue) return
    const parts: string[] = inputValue?.split(' ')?.filter(Boolean)

    let currentPosition = 0
    parts?.forEach(part => {
      const start = (inputValue as string).indexOf(part, currentPosition)
      const end = start + part.length - 1

      const isDelimited = isDelimitedPart(part)

      prevCaretPosition.current = caretPosition
      currentPosition = end + 2

      const isCaretInDelimitedPart = isDelimited && caretPosition > start && caretPosition <= end

      if (isCaretInDelimitedPart && prevCaretPosition.current >= end) {
        prevCaretPosition.current = start
        inputRef.current?.setSelectionRange(start, start)
        return
      }
      // coming from beginning
      if (isCaretInDelimitedPart && prevCaretPosition.current <= end) {
        prevCaretPosition.current = end + 1
        inputRef.current?.setSelectionRange(end + 1, end + 1)
        return
      }
    })
  }

  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const caretPosition = (event.target as HTMLInputElement)?.selectionStart
    if (!caretPosition) return

    handleCaretPosition(caretPosition)
  }

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const caretPosition = (event.target as HTMLInputElement)?.selectionStart
    if (!caretPosition) return

    handleCaretPosition(caretPosition)
  }

  const validateWordAndCharacterLimits = (value: string) => {
    const wordCount = value.trim().split(/\s+/).length || 0
    const charCount = value.length || 0

    if (minWordLimit && wordCount < minWordLimit) {
      setLocalErrorMessage(`${customErrorMessage}`)
      return true
    }

    if (maxWordLimit && wordCount > maxWordLimit) {
      setLocalErrorMessage(`${customErrorMessage}`)
      return true
    }

    if (minCharacterLimit && charCount < minCharacterLimit) {
      setLocalErrorMessage(`${customErrorMessage}`)
      return true
    }

    if (maxCharacterLimit && charCount > maxCharacterLimit) {
      setLocalErrorMessage(`${customErrorMessage}`)
      return true
    }

    if (fixedCharacterLimit && charCount !== fixedCharacterLimit) {
      setLocalErrorMessage(`${customErrorMessage}`)
      return true
    }

    if (fixedNumberLimit && charCount !== fixedNumberLimit) {
      setLocalErrorMessage(`${customErrorMessage}`)
      return true
    }

    if (minCharacterPerWord || maxCharacterPerWord) {
      const words = value.trim().split(/\s+/)

      for (const word of words) {
        if (minCharacterPerWord && word.length < minCharacterPerWord) {
          setLocalErrorMessage(`${customErrorMessage}`)
          return true
        }

        if (maxCharacterPerWord && word.length > maxCharacterPerWord) {
          setLocalErrorMessage(`${customErrorMessage}`)
          return true
        }
      }
    }
    setLocalErrorMessage('')
    return false
  }

  const handleChangeInternal = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    if (fixedCharacterLimit && value.length > fixedCharacterLimit) {
      e.target.value = value.slice(0, fixedCharacterLimit)
    }
    if (fixedNumberLimit && value.length > fixedNumberLimit) {
      e.target.value = value.slice(0, fixedNumberLimit)
    }
    validateWordAndCharacterLimits(e.target.value)
    if (handleChange) {
      handleChange(e)
    }
  }

  return (
    <div className={`flex flex-col ${isFullWidth ? 'w-full' : ''} relative ${customClass}`} data-track-id={textFieldId}>
      <p className={`flex items-center text-sm whitespace-nowrap text-gray-dark mb-1 ${labelCustomClass}`}>
        {label}
        {isRequired && <span className='text-[#CB2711]'>*</span>}
        <span className='text-xs text-gray-dark ml-auto'>
          {showCharacterCountConfig?.showCharacterCountOnLabel && showCount()}
        </span>
      </p>
      <Input
        className={`rounded shadow-sm ${isDisabled ? 'bg-gray-lighter' : ''} ${inputClass}`}
        id={textFieldId}
        required={isRequired && toolTipRequired}
        error={errorStatus || Boolean(localErrorMessage)}
        inputRef={inputRef}
        margin={inputMargin}
        InputProps={{
          autoComplete: autoComplete,
          endAdornment: (
            <InputAdornment position='end'>
              {showCharacterCountConfig &&
                !showCharacterCountConfig?.showCharacterCountOnLabel &&
                (showCharacterCountConfig?.showOnlyOnFocus ? isFocused && showCount() : showCount())}
              {endIcon}
            </InputAdornment>
          ),
          startAdornment: <InputAdornment position='start'>{startIcon}</InputAdornment>,
          inputProps: {
            ...inputProps,
            ...(inputType === 'number' && {
              inputMode: 'numeric',
              pattern: '[0-9]*'
            })
          },
          onKeyUp: handleKeyUp,
          onClick: handleInputClick
        }}
        size={inputSize}
        fullWidth={isFullWidth}
        value={inputValue}
        onChange={handleChangeInternal}
        disabled={isDisabled}
        type={inputType}
        inputMode={inputModeType}
        placeholder={inputPlaceholder}
        variant={inputVairant}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onClick={handleOnClick}
        onKeyDown={handleKeyDown}
        autoFocus={autoFocus}
        multiline={isMultiline}
      />
      <div className={`absolute inset-0 flex items-center pl-4 pointer-events-none ${Boolean(label) ? 'pt-4' : ''}`}>
        <div>{delimiters && Boolean(delimiters?.length) && getHighlightedText()}</div>
      </div>
      {errorStatus || localErrorMessage ? (
        <TextFieldError message={localErrorMessage || errorMessage} />
      ) : maxCharacter && characterCounter ? (
        <TextInputLimitCounter inputText={inputValue} limit={maxCharacter} />
      ) : (
        <></>
      )}
    </div>
  )
}

export default InputTextField
